import React, { useState, useEffect, useRef } from "react";
import { Timeline } from "vis-timeline/standalone";
import { DataSet } from "vis-data";
import "vis-timeline/styles/vis-timeline-graph2d.min.css";
import { useService } from "contexts/ServiceContextProvider";
import { useParams } from "react-router-dom";
import { useAuthorization } from 'contexts/AuthContextProvider';
import { useSidebar } from 'contexts/SidebarContextProvider';
import { Typography, Box, Checkbox, Button, FormControlLabel } from "@mui/material";
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { convertBetweenTimezones } from 'components/Custom/TimezoneConvertion';
import { useTranslation } from "react-i18next";
import '../../css/components/Calendar/TodayVisTimeline.css';
import '../../css/main.css';
import TinyCustomFallback from 'components/Custom/TinyCustomFallback';
import { DateTime } from 'luxon';
import UserFilterModal from "./TodayVisTimelineFiltering";

/* eslint-disable react-hooks/exhaustive-deps */

const TodayVisTimeline = () => {
  const { t } = useTranslation();
  const [timeline, setTimeline] = useState(null);
  const { fetchWorkspaceCalendarWithVacations, getUserTimeZone } = useService();
  const { workspaceId } = useParams();
  const timelineRef = useRef(null);
  const { getLoggedUser } = useAuthorization();
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [myUserTimezone, setMyUserTimezone] = useState(null);
  const [workspaceHolidays, setWorkspaceHolidays] = useState(null);
  const [workspaceVacations, setWorkspaceVacations] = useState(null);
  const [showHolidays, setShowHolidays] = useState(false);
  const [showVacations, setShowVacations] = useState(false);
  const [holidayCount, setHolidayCount] = useState(0);
  const [vacationCount, setVacationCount] = useState(0);
  const [currentDate, setCurrentDate] = useState(DateTime.now());
  const [avoidMisfire, setAvoidMisfire] = useState(false);
  const avoidMisfireRef = useRef(false);
  const timerRef = useRef(null);
  const { isSidebarOpen } = useSidebar();

  const [isFiltering, setIsFiltering] = useState(false);

  const generateColor = (index) => {
    const hue = (index * 137.508) % 360;
    return `hsl(${hue}, 70%, 80%)`;
  };

  useEffect(() => {
    const initializeTimeline = async () => {
      try {
        if (workspaceId) {
          const [eventsResponse, timezoneResponse] = await Promise.all([
            fetchEvents(workspaceId),
            getUserTimeZone(getLoggedUser())
          ]);
          setMyUserTimezone(timezoneResponse);

          setWorkspaceHolidays(eventsResponse.holidays);
          setWorkspaceVacations(eventsResponse.vacations);

          const { timelineGroups, timelineItems } = prepareTimelineData(
            eventsResponse.working_hours,
            timezoneResponse,
            false
          );
          const Events = { ...eventsResponse.working_hours, timelineGroups, timelineItems };

          const uniqueUsers = Array.from(
            new Set(Events.timelineItems.map((item) => item.fullName))
          );

          setUsers(uniqueUsers);
          if (selectedUsers.size === 0) {
            setSelectedUsers(new Set(uniqueUsers));
          }
          renderTimeline(Events.timelineGroups, Events.timelineItems);
        }
      } catch (error) {
        console.error("Error during initialization:", error);
      }
    };

    initializeTimeline();

    return () => {
      if (timeline) {
        setTimeline(null);
      }
    };
  }, [currentDate]);


  useEffect(() => {
    if (!timeline || !workspaceId) return;

    if (avoidMisfireRef.current) return;
  
    avoidMisfireRef.current = true;
    setIsFiltering(true);
  
    fetchEvents(workspaceId)
      .then((data) => {
        const { timelineGroups, timelineItems } = prepareTimelineData(
          data.working_hours,
          myUserTimezone,
          true
        );
        renderTimeline(timelineGroups, timelineItems);
        setIsFiltering(false);
      })
      .catch((error) => {
        console.error("Error during fetchEvents:", error);
        setIsFiltering(false);
      })
      .finally(() => {
        // Clear the misfire flag after 2 seconds
        timerRef.current = setTimeout(() => {
          avoidMisfireRef.current = false;
        }, 2000);
      });
  }, [selectedUsers, myUserTimezone, workspaceHolidays, showHolidays, showVacations]);
  

  const fetchEvents = async (workspaceId) => {
    try {
      const data = await fetchWorkspaceCalendarWithVacations(workspaceId);

      if (!data) {
        throw new Error("No data returned from fetchWorkspaceCalendar");
      }
      return data;
    } catch (error) {
      console.error("Error fetching events:", error);
      return { timelineGroups: [], timelineItems: [] };
    }
  };

  const isUserOnHoliday = (userCountry, today) => {
    return workspaceHolidays?.some(holiday => {
      const holidayDate = DateTime.fromISO(holiday.date, { zone: 'utc' }).toISODate();
      const cleanedUserCountry = userCountry ? userCountry.replace(/[()]/g, '').trim() : null;

      return holidayDate === today.toISODate() &&
        (holiday.country === cleanedUserCountry);
    });
  };


  const prepareTimelineData = (data, loggedUserTimezone, renderHolidayUsers) => {
    const timelineGroups = [];
    const timelineItems = [];
    const userColorMap = new Map();
    const currentDay = currentDate.setLocale('en-GB').toFormat('cccc');


    // Use Sets to track unique users counted for holidays and vacations
    const holidayUserSet = new Set();
    const vacationUserSet = new Set();

    // Filter data for today's working hours
    const filteredData = data.filter(item => item.day === currentDay);



    filteredData.forEach((workingHours, index) => {
      const { start, end, timezone, user_info } = workingHours;
      const fullName = user_info.full_name;
      const userCountry = user_info.location ? user_info.location.split(" ")[1] : null;

      if (!userColorMap.has(fullName)) {
        userColorMap.set(fullName, generateColor(userColorMap.size));
      }

      const userColor = userColorMap.get(fullName);
      let group = timelineGroups.find((g) => g.content === fullName);
      if (!group) {
        group = {
          id: timelineGroups.length,
          content: fullName,
        };
        timelineGroups.push(group);
      }

      const startDateTime = convertBetweenTimezones(
        `${currentDate.toISODate()}T${start}:00`,
        timezone,
        loggedUserTimezone.timezone
      );

      const endDateTime = convertBetweenTimezones(
        `${currentDate.toISODate()}T${end}:00`,
        timezone,
        loggedUserTimezone.timezone
      );


      const isHoliday = isUserOnHoliday(userCountry, currentDate);

      const isSameDay = (date1, date2) => date1.hasSame(date2, 'day');

      const isVacation = workspaceVacations?.some(vacation => {
        // Convert start and end dates to Luxon DateTime objects
        const vacationStart = DateTime.fromISO(vacation.date_start, { zone: 'utc' });
        const vacationEnd = DateTime.fromISO(vacation.date_end, { zone: 'utc' });

        return vacation.user.full_name === fullName &&
          vacationStart <= currentDate &&
          (currentDate <= vacationEnd || isSameDay(currentDate, vacationEnd));
      });



      // Add the user to the appropriate Set to count them once
      if (isVacation) {
        vacationUserSet.add(fullName); // Only add the user to vacation set once
      } else if (isHoliday) {
        holidayUserSet.add(fullName); // Only add the user to holiday set once
      }


      // Filter out users based on showHolidays and showVacations toggles
      // Priority is given to vacations
      if (renderHolidayUsers === true) {
        if ((!showHolidays && isHoliday && !isVacation) || (!showVacations && isVacation)) {
          return; // Skip if the user is on holiday or vacation and the toggle is off
        }
      }


      const formattedStart = startDateTime.toFormat('HH:mm');
      const formattedEnd = endDateTime.toFormat('HH:mm');


      const timezoneInfo = `${timezone || 'Unknown'}`;

      let tooltip = `${fullName}\nStarts @ ${formattedStart}\nEnds @ ${formattedEnd} (${timezoneInfo})`;

      if (isVacation) {
        tooltip += `<br>${t("calendar.user_on_vacation")} 🌴`;
      } else if (isHoliday) {
        tooltip += `<br>${t("calendar.user_on_holiday")}`;
      }


      if (startDateTime && endDateTime) {
        timelineItems.push({
          id: `${fullName}-${index}`,
          group: group.id,
          content: `${fullName} (${formattedStart} - ${formattedEnd} ${timezoneInfo})`,
          start: startDateTime.toISO(),
          end: endDateTime.toISO(),
          style: `background-color: ${userColor}`,
          title: tooltip,
          fullName,
          className: isVacation ? 'vacation' : (isHoliday ? 'sparkle' : ''),
        });
      } else {
        console.error(`Failed to convert time for user ${fullName} with start: ${start}, end: ${end}, timezone: ${timezone}`);
      }

    });

    setHolidayCount(holidayUserSet.size);
    setVacationCount(vacationUserSet.size);

    return { timelineGroups, timelineItems };
  };


  const navigateDay = (daysToAdd) => {
    if (avoidMisfireRef.current) return;
    avoidMisfireRef.current = true;
    setAvoidMisfire(true);
    setCurrentDate(prevDate => prevDate.plus({ days: daysToAdd }));

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      avoidMisfireRef.current = false;
      setAvoidMisfire(false);
    }, 2000);
  };

  const resetDay = () => {
    if (avoidMisfireRef.current) return;
    avoidMisfireRef.current = true;
    setAvoidMisfire(true);
    setCurrentDate(DateTime.now());

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      avoidMisfireRef.current = false;
      setAvoidMisfire(false);
    }, 2000);
  };



  const renderTimeline = (groups, items) => {
    setAvoidMisfire(true);
    const container = timelineRef.current;
    if (!container) {
      console.error("Timeline container not found during rendering");
      return;
    }

    const filteredItems = items.filter((item) =>
      selectedUsers.has(item.fullName)
    );

    const filteredGroups = groups.filter((group) =>
      filteredItems.some((item) => item.group === group.id)
    );

    const itemsDataset = new DataSet(filteredItems);
    const groupsDataset = new DataSet(filteredGroups);

    const options = {
      stack: true,
      horizontalScroll: true,
      zoomKey: "ctrlKey",
      orientation: "top",
      start: currentDate.set({ hour: 8, minute: 0 }).toISO(),
      end: currentDate.set({ hour: 20, minute: 0 }).toISO(),
      min: currentDate.startOf('day').toISO(),
      max: currentDate.endOf('day').toISO(),
    };

    if (timeline) {
      try {
        timeline.destroy();
      } catch (error) {
        console.error("Error destroying timeline:", error);
      }
    }

    try {
      const newTimeline = new Timeline(
        container,
        itemsDataset,
        groupsDataset,
        options
      );

      setTimeline(newTimeline);
    } catch (error) {
      console.error("Error creating timeline:", error);
    } finally {
      setTimeout(() => {
        setAvoidMisfire(false);
      }, 1000);
    }
  };

  const handleOpenFilterModal = () => {
    setFilterModalOpen(true);
  };

  const handleCloseFilterModal = (finalSelectedUsers) => {
    setFilterModalOpen(false);
    setSelectedUsers(finalSelectedUsers);
  };
 

  return (
    <div className="vis-calendar-main-div">
      <Typography variant="h4" className="vis-calendar-today-title">
        {t("calendar.work_hours_day")}
      </Typography>
      <Box className="vis-calendar-checkboxes">
        <FormControlLabel
          control={
            <Checkbox
              checked={showHolidays}
              onChange={(e) => setShowHolidays(e.target.checked)}
              disabled={avoidMisfire} 
            />
          }
          label={t('calendar.users_affected_holiday', { count: holidayCount })}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showVacations}
              onChange={(e) => setShowVacations(e.target.checked)}
              disabled={avoidMisfire} 
            />
          }
          label={t('calendar.users_affected_vacation', { count: vacationCount })}
        />
      </Box>
      <Box className="vis-calendar-buttons">
        <Button
          variant="contained"
          className="button-dark-bg vis-calendar-action-button"
          onClick={() => navigateDay(-1)}
          disabled={avoidMisfire}
        >
          <ArrowBack />
        </Button>
        <Button
          variant="contained"
          className="button-dark-bg vis-calendar-action-button"
          onClick={resetDay}
          disabled={avoidMisfire}
        >
          {t("calendar.today")}
        </Button>
        <Button
          variant="contained"
          className="button-dark-bg"
          onClick={() => navigateDay(1)}
          disabled={avoidMisfire}
        >
          <ArrowForward />
        </Button>
      </Box>
      {/* Wrap timeline container in a relative container */}
      <div style={{ position: "relative" }}>
        <div ref={timelineRef} className="vis-calendar-calendar" />
        {isFiltering && (
          <div
            style={{
              position: "absolute",
              width: "95%",
              margin: "0 auto",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255,255,255,0.7)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
            }}
          >
            <TinyCustomFallback />
            <Typography variant="h6">{t("project_page.loading")}</Typography>
          </div>
        )}
      </div>
      <Button
        variant="contained"
        className="button-light-bg vis-calendar-filter-users-button"
        onClick={handleOpenFilterModal}
      >
        {t("calendar.filter")}
      </Button>

      <UserFilterModal
        open={filterModalOpen}
        onClose={handleCloseFilterModal}
        users={users}
        initialSelectedUsers={selectedUsers}
        isSidebarOpen={isSidebarOpen}
      />

    </div>
  );
};

export default TodayVisTimeline;